import React from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Layout } from '../atoms-types/layout';
import { FormGroup, FormControlLabel, Switch, Button, Paper, TextField, useTheme } from '@mui/material';
import { useState } from 'react';

const ResponsiveGridLayout = WidthProvider(Responsive);

const AccountSettingsGrid: React.FC = () => {

  const layout: Layout[] = [
    { i: 'a', x: 1, y: 1, w: 1, h: 1, minW: 1, minH: 1 },
    { i: 'b', x: 1, y: 4, w: 7, h: 5, minW: 1, minH: 2 },
  ];

  const [emailChecked, setEmailChecked] = useState(false);
  const [textChecked, setTextChecked] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [editEmail, setEditEmail] = useState(false);
  const [editPhone, setEditPhone] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');

  const theme = useTheme();

  const validateEmail = (value: string) => {
    if (!value) return 'Email is required';
    // Simple email regex
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value) ? '' : 'Invalid email address';
  };

  const validatePhone = (value: string) => {
    if (!value) return 'Phone number is required';
    // US/Canada 10-digit phone number
    const re = /^\d{10}$/;
    return re.test(value.replace(/\D/g, '')) ? '' : 'Invalid phone number (10 digits)';
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailChecked(event.target.checked);
    if (!event.target.checked) {
      setEmail('');
      setEmailError('');
      setEditEmail(false);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextChecked(event.target.checked);
    if (!event.target.checked) {
      setPhone('');
      setPhoneError('');
      setEditPhone(false);
    }
  };

  const handleEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingEmail(event.target.value);
    setEmailError(validateEmail(event.target.value));
  };

  const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPhone(event.target.value);
    setPhoneError(validatePhone(event.target.value));
  };

  const handleSave = () => {
    let valid = true;
    if (editEmail) {
      const err = validateEmail(pendingEmail);
      setEmailError(err);
      if (err) valid = false;
    }
    if (editPhone) {
      const err = validatePhone(pendingPhone);
      setPhoneError(err);
      if (err) valid = false;
    }
    if (!valid) return;
    if (editEmail) {
      setEmail(pendingEmail);
      setEditEmail(false);
    }
    if (editPhone) {
      setPhone(pendingPhone);
      setEditPhone(false);
    }
    if (!editEmail && !editPhone) {
      alert(`Preferences saved!\nEmail: ${emailChecked ? email : 'Off'}\nText: ${textChecked ? phone : 'Off'}`);
    }
  };

  const isSaveDisabled = (
    (editEmail && (!!emailError || !pendingEmail)) ||
    (editPhone && (!!phoneError || !pendingPhone))
  );

  return (
    <ResponsiveGridLayout 
      className="layout" 
      layouts={{ lg: layout }} 
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
      cols={{ lg: 3, md: 3, sm: 3, xs: 1 }}
      rowHeight={100}
      draggableCancel=".cancelSelectorName"
      isDraggable={false}
      isResizable={false}
      >
      <div key="a" style={{ minHeight: "500px" }} className='card-gauge'>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
          <h1>Account Settings</h1>
        </div>
      </div>
      <div key="b" style={{ minHeight: "500px" }} className='card-gauge'>
        <Paper elevation={3} style={{ margin: '32px auto', padding: 24 }}>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={emailChecked} onChange={handleEmailChange} />}
              label="Email Notifications"
            />
            <div style={{ marginLeft: 32, marginBottom: 16 }}>
              {emailChecked ? (
                editEmail ? (
                  <>
                    <TextField
                      label="Email Address"
                      value={pendingEmail}
                      onChange={handleEmailInputChange}
                      error={!!emailError}
                      helperText={emailError}
                      fullWidth
                      margin="normal"
                      type="email"
                      autoComplete="email"
                      sx={{ maxWidth: 320 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ marginRight: 8, marginTop: 8 }}
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={() => { setEditEmail(false); setEmailError(''); }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
                      {email ? `Current email: ${email}` : 'No email set.'}
                    </span>
                    <Button
                      variant="text"
                      size="small"
                      style={{ marginLeft: 12 }}
                      onClick={() => { setEditEmail(true); setPendingEmail(email); }}
                    >
                      {email ? 'Change' : 'Add'}
                    </Button>
                    {email && (
                      <div style={{ color: theme.palette.text.primary, fontSize: 13, marginTop: 4 }}>
                        By providing your email, you agree to receive emails from ODP.Golf. Email frequency varies. View our <a href="/privacy">privacy policy</a> for more information.
                      </div>
                    )}
                  </>
                )
              ) : (
                <span style={{ color: theme.palette.text.disabled, fontSize: 15 }}>
                  {email ? `Current email: ${email}` : 'No email set.'}
                </span>
              )}
            </div>
            <FormControlLabel
              control={<Switch checked={textChecked} onChange={handleTextChange} />}
              label="Text Message Notifications"
            />
            <div style={{ marginLeft: 32 }}>
              {textChecked ? (
                editPhone ? (
                  <>
                    <TextField
                      label="Phone Number"
                      value={pendingPhone}
                      onChange={handlePhoneInputChange}
                      error={!!phoneError}
                      helperText={phoneError}
                      fullWidth
                      margin="normal"
                      type="tel"
                      autoComplete="tel"
                      placeholder="1234567890"
                      sx={{ maxWidth: 320 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ marginRight: 8, marginTop: 8 }}
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={() => { setEditPhone(false); setPhoneError(''); }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
                      {phone ? `Current phone: ${phone}` : 'No phone number set.'}
                    </span>
                    <Button
                      variant="text"
                      size="small"
                      style={{ marginLeft: 12 }}
                      onClick={() => { setEditPhone(true); setPendingPhone(phone); }}
                    >
                      {phone ? 'Change' : 'Add'}
                    </Button>
                    {phone && (
                      <div style={{ color: theme.palette.text.primary, fontSize: 13, marginTop: 4 }}>
                        By providing your phone number, you agree to receive text messages from ODP.Golf. Message and data rates may apply. Message frequency varies.<br/>
                        You can opt out anytime by replying STOP, or get help by replying HELP. View our <a href="/privacy">privacy policy</a> for more information.
                      </div>
                    )}
                  </>
                )
              ) : (
                <span style={{ color: theme.palette.text.disabled, fontSize: 15 }}>
                  {phone ? `Current phone: ${phone}` : 'No phone number set.'}
                </span>
              )}
            </div>
          </FormGroup>
          <Button variant="contained" color="primary" style={{ marginTop: 16 }} onClick={handleSave} disabled={isSaveDisabled}>
            Save
          </Button>
        </Paper>
      </div>
    </ResponsiveGridLayout>
  );
};

export default AccountSettingsGrid;